// @section 1 Campaigns module — parent shell + nested sub-route routing
// Cold-email surfaces (Overview/Funnel/Trend/Leads/Replies/Touch) ship from
// production. GA4 + Social added 2026-06-03 as placeholders so the full
// marketing-ops module set is walkable on localhost.

import { NavLink, Route, Routes, useLocation } from 'react-router-dom';
import { useIsMobile } from '../../lib/useIsMobile';
import Overview from './Overview';
import MasterDetail from './MasterDetail';
import CampaignDetail from './CampaignDetail';
import Funnel from './Funnel';
import Trend from './Trend';
import Leads from './Leads';
import Replies from './Replies';
import Touch from './Touch';
import Ga4 from './Ga4';
import Social from './Social';
import Daily from './Daily';

const TABS: { to: string; label: string; end?: boolean }[] = [
  { to: '/campaigns',          label: 'Overview', end: true },
  { to: '/campaigns/daily',    label: 'Daily' },
  { to: '/campaigns/funnel',   label: 'Funnel' },
  { to: '/campaigns/trend',    label: 'Trend' },
  { to: '/campaigns/leads',    label: 'Leads' },
  { to: '/campaigns/replies',  label: 'Replies' },
  { to: '/campaigns/touch',    label: 'Touch' },
  { to: '/campaigns/ga4',      label: 'GA4' },
  { to: '/campaigns/social',   label: 'Social' },
];

function CampaignsTabs() {
  const loc = useLocation();
  // @block 1.1 On phones the 9-tab strip wraps to a couple rows and tightens its
  // gutters rather than overflowing off-screen (the canvas clips overflow-x).
  const isMobile = useIsMobile();
  // Hide the strip on drill-in routes (master/:masterKey, :campaignId).
  const onKnownTab = TABS.some((t) => (t.end ? loc.pathname === t.to : loc.pathname.startsWith(t.to)));
  const isRoot = loc.pathname === '/campaigns';
  if (!onKnownTab && !isRoot) return null;
  return (
    <div style={{
      display: 'flex', gap: 4, padding: isMobile ? '8px 12px 0' : '10px 26px 0',
      flexWrap: isMobile ? 'wrap' : 'nowrap',
      borderBottom: '1px solid var(--t2-border)',
      background: 'rgba(10,19,34,0.35)',
      flexShrink: 0,
    }}>
      {TABS.map((t) => (
        <NavLink
          key={t.to}
          to={t.to}
          end={t.end}
          style={({ isActive }) => ({
            padding: '7px 12px',
            fontSize: 12,
            fontWeight: 600,
            color: isActive ? 'var(--tx-0)' : 'var(--tx-3)',
            textDecoration: 'none',
            borderBottom: isActive ? '2px solid var(--brand-3)' : '2px solid transparent',
            marginBottom: -1,
          })}
        >
          {t.label}
        </NavLink>
      ))}
    </div>
  );
}

export default function Campaigns() {
  return (
    <div style={{ display: 'grid', gridTemplateRows: 'auto 1fr', height: '100%', minHeight: 0 }}>
      <CampaignsTabs />
      <div style={{ minHeight: 0, overflow: 'auto' }}>
        <Routes>
          <Route index element={<Overview />} />
          <Route path="daily" element={<Daily />} />
          <Route path="funnel" element={<Funnel />} />
          <Route path="trend" element={<Trend />} />
          <Route path="leads" element={<Leads />} />
          <Route path="replies" element={<Replies />} />
          <Route path="touch" element={<Touch />} />
          <Route path="ga4" element={<Ga4 />} />
          <Route path="social" element={<Social />} />
          <Route path="master/:masterKey" element={<MasterDetail />} />
          <Route path=":campaignId" element={<CampaignDetail />} />
        </Routes>
      </div>
    </div>
  );
}
